'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

type AppBootLifecycleValue = {
  claimExperience: (key: string) => boolean;
};

const AppBootLifecycleContext = createContext<AppBootLifecycleValue | null>(null);

/**
 * Owns boot-only experiences above the pathname-keyed route subtree. A dashboard
 * route may replace or remount its local shell, but it cannot turn ordinary
 * navigation (or a PWA resume) into a second application boot.
 */
export function AppBootLifecycleProvider({ children }: { children: React.ReactNode }) {
  const claimedExperiences = useRef(new Set<string>());

  const claimExperience = useCallback((key: string) => {
    if (claimedExperiences.current.has(key)) return false;

    try {
      if (window.sessionStorage.getItem(key) === 'true') {
        claimedExperiences.current.add(key);
        return false;
      }
    } catch {
      // iOS can deny Web Storage in constrained/private contexts. The in-memory
      // lifecycle still prevents route remounts and app resumes from replaying.
    }

    claimedExperiences.current.add(key);
    try {
      window.sessionStorage.setItem(key, 'true');
    } catch {
      // The provider-owned in-memory claim remains authoritative for this boot.
    }
    return true;
  }, []);

  const value = useMemo(() => ({ claimExperience }), [claimExperience]);

  return (
    <AppBootLifecycleContext.Provider value={value}>{children}</AppBootLifecycleContext.Provider>
  );
}

export function useAppBootLifecycle() {
  return useContext(AppBootLifecycleContext);
}
